import { adminApi, ensureArray } from '../services/api';
import { useState, useEffect } from 'react';
import { TherapistSpecializationPills } from '../components/TherapistSpecializationPills';
import { formatTherapistLanguages } from '@lighthouse/shared';

interface PendingTherapist {
  id: string;
  cassonNumber: string;
  licenseBody: string;
  specializations: string[];
  languages: string[];
  status: string;
  user: { email: string; phone: string; createdAt: string };
  credentialDocs: { id: string; docType: string; r2Key: string }[];
}

interface CassonResult {
  valid: boolean;
  name?: string;
  licenseType?: string;
  expiryDate?: string;
  source: 'casson_api' | 'manual';
}

export function TherapistVerificationPage() {
  const [therapists, setTherapists] = useState<PendingTherapist[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    adminApi<PendingTherapist[]>('/admin/therapists/pending')
      .then((data) => setTherapists(ensureArray(data)))
      .catch(() => setTherapists([]))
      .finally(() => setLoading(false));
  }, []);

  const [rejectReason, setRejectReason] = useState<Record<string, string>>({});
  const [cassonResults, setCassonResults] = useState<Record<string, CassonResult>>({});
  const [cassonLoading, setCassonLoading] = useState<Record<string, boolean>>({});

  const handleVerify = async (id: string, status: 'verified' | 'suspended') => {
    const reason = rejectReason[id];
    if (status === 'suspended' && !reason?.trim()) {
      alert('Please enter a rejection reason before suspending.');
      return;
    }
    try {
      await adminApi(`/admin/therapists/${id}/verify`, {
        method: 'PUT',
        body: JSON.stringify({ status, reason }),
      });
      setTherapists((prev) => prev.filter((t) => t.id !== id));
    } catch {}
  };

  const checkCasson = async (id: string, cassonNumber: string) => {
    setCassonLoading((prev) => ({ ...prev, [id]: true }));
    try {
      const data = await adminApi<CassonResult>(
        `/admin/therapists/casson/${encodeURIComponent(cassonNumber)}`
      );
      setCassonResults((prev) => ({ ...prev, [id]: data }));
    } catch {
      setCassonResults((prev) => ({ ...prev, [id]: { valid: false, source: 'manual' } }));
    } finally {
      setCassonLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  const openDoc = async (r2Key: string) => {
    try {
      const data = await adminApi<{ signedUrl?: string }>(
        `/admin/documents/signed-url?key=${encodeURIComponent(r2Key)}`
      );
      if (data.signedUrl) window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    } catch {}
  };

  if (loading) return <div className="text-center text-gray-500">Loading...</div>;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900">Therapist Verification</h1>

      {therapists.length === 0 ? (
        <div className="card mt-6 text-center text-gray-500">No pending verifications.</div>
      ) : (
        <div className="mt-6 space-y-4">
          {therapists.map((t) => (
            <div key={t.id} className="card space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">{t.user.email}</p>
                  <p className="text-sm text-gray-500">{t.user.phone}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-sm text-gray-700">
                      {t.licenseBody}: {t.cassonNumber}
                    </span>
                    <button
                      onClick={() => checkCasson(t.id, t.cassonNumber)}
                      disabled={cassonLoading[t.id]}
                      className="rounded border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700 transition-colors hover:bg-indigo-100"
                    >
                      {cassonLoading[t.id] ? 'Checking…' : 'Check license'}
                    </button>
                    {cassonResults[t.id] && (
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-medium ${cassonResults[t.id]!.source === 'manual' ? 'bg-yellow-100 text-yellow-800' : cassonResults[t.id]!.valid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}
                      >
                        {cassonResults[t.id]!.source === 'manual'
                          ? '⚠ Manual verification required'
                          : cassonResults[t.id]!.valid
                            ? `✓ Valid — ${cassonResults[t.id]!.name}`
                            : '✗ Not found in registry'}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    <TherapistSpecializationPills specializations={t.specializations} />
                  </div>
                  {t.languages.length > 0 && (
                    <p className="mt-2 text-xs text-gray-500">
                      Languages: {formatTherapistLanguages(t.languages)}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-gray-400">
                    Registered: {new Date(t.user.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="ml-4 flex min-w-[100px] flex-col gap-2">
                  <button
                    onClick={() => handleVerify(t.id, 'verified')}
                    className="btn-primary text-sm"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleVerify(t.id, 'suspended')}
                    className="rounded-lg bg-red-600 px-3 py-1 text-sm text-white hover:bg-red-700"
                  >
                    Reject
                  </button>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-gray-500">
                  Rejection reason <span className="text-gray-400">(required to reject)</span>
                </label>
                <textarea
                  rows={2}
                  value={rejectReason[t.id] ?? ''}
                  onChange={(e) => setRejectReason((prev) => ({ ...prev, [t.id]: e.target.value }))}
                  placeholder="e.g. license number not verifiable with CASSON/NACP, documents unclear…"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                />
              </div>

              {t.credentialDocs.length > 0 && (
                <div className="mt-4 border-t pt-4">
                  <p className="text-sm font-medium text-gray-700">Documents</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {t.credentialDocs.map((doc) => (
                      <button
                        key={doc.id}
                        onClick={() => openDoc(doc.r2Key)}
                        className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs text-indigo-700 transition-colors hover:bg-indigo-100"
                        title={`View ${doc.docType}`}
                      >
                        📄 {doc.docType}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
